import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Play, X, AlertTriangle } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { logError } from '@/lib/logger';

export default function VideoDemoSection() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.2 });
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Callback ref to auto-play video when lightbox mounts it into the DOM.
  // autoPlay attribute alone fails because React's async render breaks the
  // browser's "user gesture" window, so we call play() programmatically.
  const videoMountRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node) {
      const tryPlay = () => {
        node.play().catch(() => {
          // Browser blocked autoplay — controls are visible for manual play
        });
      };
      if (node.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        tryPlay();
      } else {
        node.addEventListener('canplay', tryPlay, { once: true });
      }
    }
  }, []);

  const openLightbox = useCallback(() => {
    setVideoFailed(false);
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  // If the <video> source fails to load (CDN 404, codec, mobile data, etc.)
  // we surface a friendly fallback rather than the native browser "Your
  // browser does not support the video element" string — which PostHog
  // flagged as a dead-click target on the landing page.
  const handleVideoError = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget;
    logError('Demo video failed to load', {
      networkState: el.networkState,
      errorCode: el.error?.code,
      errorMessage: el.error?.message,
    });
    setVideoFailed(true);
  }, []);

  // Escape key listener + body scroll lock
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen, closeLightbox]);

  return (
    <>
      <section
        ref={sectionRef}
        id="video-demo-section"
        className="pt-12 pb-4 bg-white dark:bg-gray-900"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div
            className={`text-center mb-8 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              See CodePulse in Action
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              A 90-second walkthrough of your engineering health dashboard
            </p>
          </div>

          {/* Video Container */}
          <div
            className={`relative ${
              isInView ? 'animate-fade-in-up animation-delay-150' : 'opacity-0'
            }`}
          >
            {/* Video wrapper with aspect ratio */}
            <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-800">
              {/* Cover image thumbnail */}
              <img
                src="/demo-cover.webp"
                alt="CodePulse dashboard demo preview"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Play button overlay - full area click target */}
              <button
                onClick={openLightbox}
                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/25 transition-colors duration-300 cursor-pointer group"
                aria-label="Play demo video"
              >
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-24 h-24 rounded-full bg-white/20 motion-safe:animate-ping" aria-hidden="true" />
                  <div className="relative flex items-center justify-center w-24 h-24 bg-white/25 group-hover:bg-white/40 rounded-full transition-all duration-300 border-2 border-white/60 group-hover:border-white/90 group-hover:scale-110">
                    <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
                  </div>
                </div>
              </button>

              {/* Text overlay on thumbnail */}
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none">
                <p className="text-white text-base sm:text-lg font-semibold drop-shadow-lg">
                  See your team's velocity in 90 seconds
                </p>
                <p className="text-white/70 text-xs sm:text-sm mt-1 drop-shadow">
                  No signup required
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Lightbox Modal */}
      {isLightboxOpen && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop - click to dismiss */}
          <button
            type="button"
            className="absolute inset-0 bg-black/90 animate-fade-in cursor-default"
            onClick={closeLightbox}
            aria-label="Close video"
          />

          {/* Close button - top right */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-[61] p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
            aria-label="Close video"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Video container */}
          <div className="relative z-[61] w-full max-w-5xl mx-4 animate-scale-in">
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              {videoFailed ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white bg-cover bg-center"
                  style={{ backgroundImage: 'url(/demo-cover.webp)' }}
                  role="alert"
                >
                  <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
                  <div className="relative flex flex-col items-center gap-3 px-6 text-center max-w-md">
                    <AlertTriangle className="w-10 h-10 text-amber-400 pointer-events-none" aria-hidden="true" />
                    <h3 className="text-xl font-semibold">We couldn&apos;t load the demo video</h3>
                    <p className="text-sm text-white/80">
                      Your network may have blocked it. Read a quick walkthrough instead, or refresh to retry.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3 mt-1">
                      <a
                        href="/how-it-works"
                        className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        See how it works
                      </a>
                      <button
                        type="button"
                        onClick={() => setVideoFailed(false)}
                        className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- demo video with audio narration, captions not yet available
                <video
                  ref={videoMountRef}
                  className="w-full h-full"
                  src="/demo.mp4"
                  poster="/demo-cover.webp"
                  controls
                  playsInline
                  preload="auto"
                  onEnded={closeLightbox}
                  onError={handleVideoError}
                >
                  <span className="pointer-events-none">Your browser does not support the video element.</span>
                </video>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
